import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Clock from "react-live-clock";
import {
  Phone,
  Mail,
  Youtube,
  Facebook,
  Instagram,
  Linkedin,
  LogOut,
  User,
  Menu,
  X,
} from "lucide-react";
import { baseUrl } from "../../utils/url";
import useLogo from "../../hooks/useLogo";

const Header = () => {
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: logoUrl } = useLogo();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="bg-white shadow-sm">
      {/* Top header with contact info and social links */}
      <div className="bg-gray-50 px-4 py-2 hidden lg:block">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-gray-700">
              <Phone size={18} className="text-primary mr-2" />
              <span className="font-medium">01890-227377</span>
            </div>
            <div className="flex items-center text-gray-700">
              <Mail size={18} className="text-primary mr-2" />
              <span className="font-medium">nnabajibon@gmail.com</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <a
              href="https://www.facebook.com/nnsfamilyofficial"
              target="_blank"
              rel="noreferrer"
              className="text-gray-500 hover:text-red-500 transition-colors"
            >
              <Youtube size={20} />
            </a>
            <a
              href="https://www.facebook.com/nnsfamilyofficial"
              target="_blank"
              rel="noreferrer"
              className="text-gray-500 hover:text-blue-600 transition-colors"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://www.facebook.com/nnsfamilyofficial"
              target="_blank"
              rel="noreferrer"
              className="text-gray-500 hover:text-pink-600 transition-colors"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.facebook.com/nnsfamilyofficial"
              target="_blank"
              rel="noreferrer"
              className="text-gray-500 hover:text-blue-700 transition-colors"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </div>
      </div>

      {/* Main header with logo, navigation, and user controls */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo & Organization Name */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0 flex items-center">
                <img
                  className="h-12 w-auto"
                  src={logoUrl || "/logo.png"}
                  alt="Logo"
                />
              </Link>
            </div>
            <div className="text-xl md:text-2xl font-bold text-primary">
              <Link to="/" className="flex flex-col">
                <span className="text-sm md:text-lg tracking-widest whitespace-nowrap">
                  নিঃস্বার্থ নবজীবন সংগঠন
                </span>
                <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">
                  Niswartha Nabajibon Sanggathan (NNS)
                </span>
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4">
            <Clock
              className="text-gray-700 font-medium mr-4"
              format={"dddd, MMMM DD, YYYY, h:mm:ss A"}
              ticking={true}
            />

            {!user?.token ? (
              <div className="flex">
                <Link
                  to="/register"
                  className="bg-primary text-white font-medium px-4 py-2 rounded-l-md hover:bg-primary/90 transition-colors"
                >
                  Sign Up
                </Link>
                <Link
                  to="/login"
                  className="bg-secondary text-white font-medium px-4 py-2 rounded-r-md hover:bg-secondary/90 transition-colors"
                >
                  Login
                </Link>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                {user?.user?.role && (
                  <Link
                    to="/dashboard"
                    className="bg-primary text-white font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Dashboard
                  </Link>
                )}

                <div className="flex items-center space-x-2">
                  <div className="flex flex-col items-end">
                    <span className="font-medium text-gray-800">
                      {user?.user?.name}
                    </span>
                    <button
                      onClick={() => {
                        localStorage.removeItem("user");
                        navigate("/login");
                      }}
                      className="text-red-500 text-sm flex items-center hover:text-red-600"
                    >
                      <LogOut size={14} className="mr-1" />
                      Sign Out
                    </button>
                  </div>
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary">
                    {user?.user?.imageUrl ? (
                      <img
                        src={`${baseUrl}${user?.user?.imageUrl}`}
                        alt={user?.user?.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <User size={20} className="text-gray-500" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Profile & Menu */}
          <div className="lg:hidden flex items-center space-x-3">
            {user?.token && (
              <Link
                to="/dashboard"
                className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary flex-shrink-0"
              >
                {user?.user?.imageUrl ? (
                  <img
                    src={`${baseUrl}${user?.user?.imageUrl}`}
                    alt={user?.user?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <User size={16} className="text-gray-500" />
                  </div>
                )}
              </Link>
            )}
            <button
              className="text-gray-700 focus:outline-none"
              onClick={toggleMenu}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-700">
                  <Phone size={18} className="text-primary mr-2" />
                  <span className="font-medium">01890-227377</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Mail size={18} className="text-primary mr-2" />
                  <span className="font-medium">nnabajibon@gmail.com</span>
                </div>
              </div>

              <div className="flex justify-center space-x-6 py-2">
                <a
                  href="https://www.facebook.com/nnsfamilyofficial"
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-500 hover:text-red-500"
                >
                  <Youtube size={20} />
                </a>
                <a
                  href="https://www.facebook.com/nnsfamilyofficial"
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-500 hover:text-blue-600"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.facebook.com/nnsfamilyofficial"
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-500 hover:text-pink-600"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.facebook.com/nnsfamilyofficial"
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-500 hover:text-blue-700"
                >
                  <Linkedin size={20} />
                </a>
              </div>

              <Clock
                className="text-gray-700 font-medium text-center"
                format={"dddd, MMMM DD, YYYY, h:mm:ss A"}
                ticking={true}
              />

              {!user?.token ? (
                <div className="flex">
                  <Link
                    to="/register"
                    className="bg-primary text-white font-medium px-4 py-2 text-center w-1/2 rounded-l-md"
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/login"
                    className="bg-secondary text-white font-medium px-4 py-2 text-center w-1/2 rounded-r-md"
                  >
                    Login
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-center">
                    <span className="font-medium text-gray-800">
                      {user?.user?.name}
                    </span>
                  </div>

                  <div className="flex space-x-2">
                    {user?.user?.role && (
                      <Link
                        to="/dashboard"
                        className="bg-primary text-white font-medium px-4 py-2 rounded-md text-center flex-1"
                      >
                        Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        localStorage.removeItem("user");
                        navigate("/login");
                      }}
                      className="bg-red-500 text-white font-medium px-4 py-2 rounded-md flex items-center justify-center flex-1"
                    >
                      <LogOut size={16} className="mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
